import React from "react";
import { Box } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import StepperControls from "@components/StepperControls/StepperControls";
import { useNotification } from "@context/NotificationContext";
import { useBayHireMyCompanyDetails } from "@features/bay-hire/hooks/useBayHireQueries";
import { useBayHireRequestForm } from "@features/bay-hire/hooks/useBayHireRequestForm";
import {
  BAY_PREFERENCE_OPTIONS,
  BAY_HIRE_REQUEST_FORM_FIELDS as F,
  PAYMENT_TYPE_OPTIONS,
  type BayHireRequestFormData,
} from "@features/bay-hire/schema/bayHireRequestFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BayHireCompanyDetailsSection from "./BayHireCompanyDetailsSection";
import BayHireCreateAttachmentUpload from "./BayHireCreateAttachmentUpload";
import BaySelectionSection from "./BaySelectionSection";

export type { BayHireRequestFormData } from "@features/bay-hire/schema/bayHireRequestFormSchema";
export type { BayPreference, BayHirePort, PaymentType } from "@features/bay-hire/constants";

interface BayHireRequestFormProps {
  onSubmit: (data: BayHireRequestFormData) => void | Promise<void>;
  onCancel?: () => void;
  disabled?: boolean;
  initialValues?: Partial<BayHireRequestFormData>;
  submitLabel?: string;
  /**
   * DFT-0044 slice V (F1, D43 revised): true for the Vendor "Update Request" screen. Locks
   * Payment type read-only while leaving dates/responsible-person/remarks AND the Bays table
   * itself editable - the domain's `VendorUpdateRequest` contract still omits `paymentType`
   * entirely (immutability is load-bearing: editing it post-submission would let a vendor dodge
   * the Exempted HOD requirement), but accepts a replacement `bayAssignments` list. Distinct from
   * `disabled`, which locks the WHOLE form including submit.
   *
   * DFT-0061: `bayPreference` is NO LONGER part of this lock - see `hasSelectedBays`'s own doc
   * above and this field's own `disabled` expression below, which now uses the same
   * `lockBayPreferenceOnSelection`-driven toggle Create already uses instead of an unconditional
   * lock (the domain now accepts a bayPreference change when combined with a bay replacement, or
   * when the list is empty).
   *
   * DFT-0044 (Update Request fix): the Attachments upload is NO LONGER included in this lock -
   * see `requestId` below and `BayHireCreateAttachmentUpload.tsx`'s own note. Only `paymentType`
   * stays genuinely immutable; the HOD document is not, and now neither is `bayPreference`.
   */
  lockImmutableFields?: boolean;
  /**
   * Present on Update and, since the DFT-0088 addendum (2026-09-01), Resubmit - the two flows
   * that prefill the Attachments upload with a document already tied to an existing request,
   * rather than starting from a blank slate like Create/Renew. Used by
   * `BayHireCreateAttachmentUpload.tsx` purely as the signal to disable its drop zone once that
   * prefilled document is attached (AC8/AC17); it does NOT change which endpoint the upload
   * itself goes through - every flow always stages the blob only, regardless of `requestId` (see
   * that component's own class doc).
   */
  requestId?: string;
  /**
   * Defaults to `true`: once at least one bay is on the table, `bayPreference` (Request Bay)
   * locks - see `hasSelectedBays`'s own doc below. Set `false` on Resubmit and Renew, whose forms
   * are deliberately fully editable even with prefilled bays (DFT-0016 working/07-technical-
   * approach.md D3: "Company can modify pre-filled dates, bays, contact; not locked to original
   * values") - unlike a fresh Create, there is no moment here where the vendor picks a
   * `bayPreference` before any bay exists, so locking it the instant the prefilled bays appear
   * would leave it locked from first render, which is not "fully editable".
   */
  lockBayPreferenceOnSelection?: boolean;
}

const BayHireRequestForm: React.FC<BayHireRequestFormProps> = ({
  onSubmit,
  onCancel,
  disabled,
  initialValues,
  submitLabel,
  lockImmutableFields,
  requestId,
  lockBayPreferenceOnSelection = true,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  // Prefill support (2026-08-05): shares BayHireCompanyDetailsSection's own query (React Query
  // dedupes by key - this is not a second network call), purely so useBayHireRequestForm can seed
  // "Name of Port company responsible" / "Designation in the company" from it. See that hook's own
  // doc for why this cannot just be part of `defaultValues` (the query resolves asynchronously).
  const { data: companyDetails } = useBayHireMyCompanyDetails();
  const { form, isFormDisabled, handleDateFieldChange } = useBayHireRequestForm({
    initialValues,
    disabled,
    companyDetails,
  });

  const { handleSubmit, formState, watch } = form;
  const immutableFieldsDisabled = isFormDisabled || !!lockImmutableFields;
  // Once at least one bay is on the table, `bayPreference` (Request Bay) must stay locked - the
  // Bays table only offers options matching the current preference (BaySelectionSection's own
  // `bayPreference` filter), so changing it out from under an existing selection would leave rows
  // on the table that no longer match what "Request Bay" says, with nothing forcing the vendor to
  // notice or reconcile it before submitting.
  const hasSelectedBays =
    lockBayPreferenceOnSelection && (watch(F.BAY_ASSIGNMENTS)?.length ?? 0) > 0;
  const dateFromValue = watch(F.DATE_FROM);
  const dateFromDate = dateFromValue ? new Date(dateFromValue) : undefined;
  const today = new Date();

  const handleSubmitForm = async (data: BayHireRequestFormData) => {
    await onSubmit(data);
  };

  // DFT-0074: matches the platform-wide handleSubmit(onValid, onInvalid) pattern (Gatepass,
  // vessel-call/create, port-security, vessel-register, deficiencies) - a client-side validation
  // failure previously only set the relevant field's inline error (e.g. BaySelectionSection's
  // `error` prop below); handleSubmitForm (and any notify() inside it) never ran, so nothing told
  // the vendor to look for it. Fires once per failed submit attempt regardless of how many fields
  // failed, same as every sibling's own onInvalid.
  const handleInvalid = () => {
    notify({
      title: t("bay-hire.request-submitted-error-title"),
      description: t("bay-hire.request-submitted-error-description"),
      type: "error",
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleSubmitForm, handleInvalid)}
      // Widened from 600 - that fit a single-column stacked layout; the wireframe's
      // "Required information" card is two columns and visibly wider than that.
      // Megaplan (2026-08-05): `mx: "auto"` centers it - a `maxWidth` alone only constrains the
      // form's width, it does not center it within the full-width page wrapper it renders inside
      // (BayHireContainer's `px-24` div), so the form previously hugged the left edge. Mirrors the
      // `mx-auto` centering convention every other Marhub single-column request form already uses
      // (e.g. VendorLicenseRequestPage's `w-full max-w-[980px] mx-auto`).
      sx={{ maxWidth: 800, mx: "auto" }}
      data-testid="bay-hire-request-form"
    >
      {/* `03_Bay Hire License.png`: ONE heading, "Bay Hire Request" - rendered at the page
          Subheader level (BayHireContainer), not duplicated here. This component previously
          rendered its own "Bay Hire Request Form" heading on top of that, which was both a
          different wording and a second heading the wireframe does not have. */}

      {/* `03_Bay Hire License.png`: everything from the company fields through Remarks lives in
          ONE bordered "Required information" card - it did not before this correction, sitting
          instead as a separate bordered company box followed by an unbordered flat field list.
          FormSection is the SAME shared component every other feature's forms already use for
          this exact bordered-card-with-header-bar pattern (VehicleDetails, JobDetails, etc.) -
          not something built new for this form. Company fields are outside THIS form's own
          FormProvider deliberately (DFT-0111: they render via FormItem too now, but through their
          own small, local, non-submitted FormProvider/useForm instance - see
          BayHireCompanyDetailsSection's own class doc) - that has no bearing on FormSection's
          grid, which only cares about DOM order, not form state - disabled and RHF-registered
          fields share this one grid without friction. */}
      <FormSection title={t("bay-hire.form.requiredInformation")}>
        {/* DFT-0044 (FE-01/FE-02, Q13): `03_Bay Hire License.png`'s six read-only company fields,
            first in the form. */}
        <BayHireCompanyDetailsSection disabled={isFormDisabled} />

        <FormProvider {...form}>
          <FormItem<BayHireRequestFormData, typeof F.RESPONSIBLE_PERSON_NAME>
            required
            name={F.RESPONSIBLE_PERSON_NAME}
            type="text"
            // Megaplan (2026-08-05): reworded to match the detail page's own CompanyDetailsCard
            // wording for the same field (bay-hire.detail.responsibleCompanyPerson) - the form
            // used a different, out-of-sync label for the identical concept.
            label={t("bay-hire.form.responsiblePersonName")}
            testId="bay-hire-form-responsible-person-name"
            disabled={isFormDisabled}
          />

          <FormItem<BayHireRequestFormData, typeof F.RESPONSIBLE_PERSON_DESIGNATION>
            required
            name={F.RESPONSIBLE_PERSON_DESIGNATION}
            type="text"
            // Megaplan (2026-08-05): matches the detail page's own CompanyDetailsCard wording
            // (bay-hire.detail.responsibleDesignation) for the same field.
            label={t("bay-hire.form.responsiblePersonDesignation")}
            testId="bay-hire-form-responsible-person-designation"
            disabled={isFormDisabled}
          />

          <FormItem<BayHireRequestFormData, typeof F.DATE_FROM>
            required
            name={F.DATE_FROM}
            type="date-picker"
            combinedInput
            // Megaplan (2026-08-05, item 3): a vendor never picks a TIME for the hire period, only
            // the date - this field previously showed a time selector it had no use for
            // (FE-08 originally rendered `DD/MM/YYYY HH:MM` to match a wireframe reading, but no
            // wireframe screen actually shows a time component here). The submitted instant still
            // normalizes to local start-of-day (useBayHireRequestForm.tsx's own toISOInstant), so
            // the OffsetDateTime contract is unaffected - only the picker UI changes.
            timePicker={false}
            label={t("bay-hire.form.dateFrom")}
            testId="bay-hire-form-date-from"
            disabled={isFormDisabled}
            onFieldChange={(key, value) => handleDateFieldChange(key, value)}
            // A hire period cannot start in the past - matches the FOTT "booking from" convention
            // (dateConstraint disables past days in the calendar, minDate belt-and-braces guards
            // manual keyboard entry). The schema's own superRefine catches anything that still
            // slips through (e.g. a stale initialValues seed).
            dateConstraint="not-past"
            minDate={today}
          />

          <FormItem<BayHireRequestFormData, typeof F.DATE_TO>
            required
            name={F.DATE_TO}
            type="date-picker"
            combinedInput
            // Megaplan (2026-08-05, item 3): see the Date From field's identical note above.
            timePicker={false}
            label={t("bay-hire.form.dateTo")}
            testId="bay-hire-form-date-to"
            disabled={isFormDisabled}
            onFieldChange={(key, value) => handleDateFieldChange(key, value)}
            // Date To can never precede Date From (mirrors BayHireRequestValidator's own rule,
            // "date to must be after or equal to date from") - floors at Date From once chosen,
            // and at today otherwise, so it can never independently be backdated either. Matches
            // FOTT's identical `minDate={bookingFrom instanceof Date ? bookingFrom : new Date()}`
            // convention for a From/To pair.
            minDate={dateFromDate ?? today}
          />

          {/* FE-03: relabelled "Bay Preference" -> "Request Bay" to match the wireframe's copy, and
            repositioned to sit directly above the Bays table it filters - it was previously first
            in the form, well before the table whose contents it governs. testId is kept stable
            (bay-hire-form-bay-preference) rather than renamed: it is an internal identifier, not
            user-facing copy, and dozens of existing tests key off it. */}
          <FormItem<BayHireRequestFormData, typeof F.BAY_PREFERENCE>
            required
            name={F.BAY_PREFERENCE}
            type="select"
            options={[...BAY_PREFERENCE_OPTIONS]}
            label={t("bay-hire.form.bayPreference")}
            testId="bay-hire-form-bay-preference"
            // Locked once a bay is on the table (see hasSelectedBays' own doc above) - a vendor
            // must remove every selected bay before switching Inside/Outside Port, rather than
            // being allowed to leave stale rows that no longer match the new preference.
            // DFT-0061: uses isFormDisabled, NOT immutableFieldsDisabled - bayPreference is no
            // longer one of Update Request's unconditionally-locked fields (the backend now
            // accepts a change when combined with a bay replacement, or when the list is empty).
            // paymentType keeps immutableFieldsDisabled below, unaffected.
            disabled={isFormDisabled || hasSelectedBays}
          />

          {/* md:col-span-2: the Bays table is full width in the wireframe, same as the company name
            field above and Remarks below. DFT-0044 slice C: the Shipping Agent's own bay
            selection, replacing the free `Number of Bays` input and its helper text.
            numberOfBays is DERIVED server-side from this list now, so a typed count would be
            silently discarded - and the helper's claim that it is "Shown to Operations as a
            reference during pre-approval" is no longer true, because Operations does not select
            bays at all. */}
          <div className="md:col-span-2">
            <BaySelectionSection
              port={watch(F.PORT)}
              bayPreference={watch(F.BAY_PREFERENCE)}
              value={watch(F.BAY_ASSIGNMENTS)}
              onChange={(bays) =>
                form.setValue(F.BAY_ASSIGNMENTS, bays, { shouldValidate: true, shouldDirty: true })
              }
              error={formState.errors.bayAssignments?.message}
              // DFT-0044 (D43 revised): bayAssignments is editable even when lockImmutableFields is
              // set - unlike bayPreference/paymentType, it is NOT locked on the Update Request
              // screen any more. Bays are still added/removed within the locked bayPreference
              // filter above (BaySelectionSection's own `bayPreference` prop), matching the
              // backend's still-immutable bayPreference exactly.
              disabled={isFormDisabled}
            />
          </div>

          <FormItem<BayHireRequestFormData, typeof F.PAYMENT_TYPE>
            required
            name={F.PAYMENT_TYPE}
            type="select"
            options={[...PAYMENT_TYPE_OPTIONS]}
            label={t("bay-hire.form.paymentType")}
            testId="bay-hire-form-payment-type"
            disabled={immutableFieldsDisabled}
          />

          {/* md:col-span-2: Remarks is full width in the wireframe. */}
          <div className="md:col-span-2">
            <FormItem<BayHireRequestFormData, typeof F.REMARKS>
              name={F.REMARKS}
              type="textarea"
              label={t("bay-hire.form.remarks")}
              testId="bay-hire-form-remarks"
              disabled={isFormDisabled}
              minRows={4}
            />
          </div>
        </FormProvider>
      </FormSection>

      {/* DFT-0044 `03_Bay Hire License.png`: OUTSIDE the "Required information" card, just above
          Cancel/Submit - matching the wireframe's layout, and its own section rather than a FormItem
          field, because it stages the file immediately rather than waiting for form Submit. */}
      <BayHireCreateAttachmentUpload
        paymentType={watch(F.PAYMENT_TYPE)}
        value={watch(F.ATTACHMENTS)?.[0] ?? null}
        onChange={(staged) =>
          form.setValue(F.ATTACHMENTS, staged ? [staged] : [], {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={formState.errors.attachments?.message}
        // DFT-0044 (Update Request fix): only `isFormDisabled`, NOT `immutableFieldsDisabled` -
        // the HOD document is not one of the genuinely immutable fields (bayPreference/
        // paymentType). See this component's own class doc and BayHireCreateAttachmentUpload.tsx's.
        disabled={isFormDisabled}
        requestId={requestId}
      />

      {/* DFT-0093: matches `03_Bay Hire License.png` (Cancel left/text, Submit right/contained
          +icon, divider above) by reusing the same `StepperControls` component every other
          single-step form (e.g. AddPortService.tsx) already uses for this job, instead of a
          bespoke button row. The `py-6 border-t` wrapper (same class string/color token as that
          precedent) supplies both the divider and the bottom spacing DFT-0092 asked for.
          `complete` triggers `handleSubmit` directly, matching every other `StepperControls`
          call site in this codebase (none of them rely on the button's native submit type) - the
          `<form>`'s own `onSubmit` above stays in place for Enter-key submission and this
          component's existing `fireEvent.submit(...)`-based tests. */}
      <div className="py-6 border-t border-t-(--stroke-secondary-stroke,#E6EDF2)">
        <StepperControls
          // Cancel has no `disabled`/pending-style prop of its own on `StepperControls` - guard it
          // inline so it keeps the same "no-op while the form is busy" behavior the hand-rolled
          // `disabled={isFormDisabled}` Cancel button had.
          cancel={onCancel && (() => !isFormDisabled && onCancel())}
          // DFT-0074: same onInvalid as the form's own onSubmit above - a vendor submitting via
          // this button must see the validation-error toast too, not only via Enter-key/native
          // form submission.
          complete={handleSubmit(handleSubmitForm, handleInvalid)}
          completeLabelTranslationKey={submitLabel ?? t("bay-hire.form.submit")}
          completePending={isFormDisabled}
        />
      </div>
    </Box>
  );
};

export default BayHireRequestForm;
